import type { ReactNode } from 'react';
import { Stack } from '../../primitives/Stack/Stack';
import { Text } from '../../atoms/Text/Text';
import styles from './SidebarItemGroup.module.css';

/**
 * Figma: `Sidebar Item Wrapper` (48:2889 and siblings) plus its optional
 * `Section Text` label. Rows are stacked with a `space/2` gap; the label sits
 * above them with the same horizontal inset as a row's content.
 *
 * The first group in the sidebar has no label, which is why `label` is optional.
 *
 * The 2026-09 update tightened this: the row gap went 4 → 2, and the label went
 * `Text xs/Medium` → `Text xs/Regular`, which is a style the file only started
 * cutting in the same update. The `space/2` the gap gave up reappears as the
 * label's own bottom padding, so `Section Text` is 12 tall rather than 10 — see
 * the stylesheet.
 */
export type SidebarItemGroupProps = {
  label?: string;
  className?: string;
  children?: ReactNode;
};

export function SidebarItemGroup({ label, className, children }: SidebarItemGroupProps) {
  return (
    <Stack as="section" direction="column" gap="2" fullWidth className={className}>
      {label !== undefined && (
        <span className={styles.sectionText}>
          <Text size="xs" leading="none" weight="regular" color="tertiary">
            {label}
          </Text>
        </span>
      )}
      {children}
    </Stack>
  );
}
